// src/store/api/axiosBaseQuery.ts
import axios, { AxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios';
import { toast } from 'react-toastify';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { RootState } from '@store/store';
import type { Store } from '@reduxjs/toolkit';
import { logout } from '@store/slices/auth/authSlice';
import { API_BASE_URL } from '@api/baseUrl';
import i18n from '../i18n';
import { startApiPerformanceTrace, finishApiPerformanceTrace } from '@/lib/firebase/performance-api';

let _store: Store | null = null;

export const injectStore = (store: Store) => {
  _store = store;
};

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

type AppAxiosRequestConfig = AxiosRequestConfig & {
  skipErrorToast?: boolean;
};

const normalizeLocale = (value: unknown): string => {
  if (typeof value !== 'string') return 'en';
  const normalized = value.trim().toLowerCase().replace('_', '-');
  if (!normalized) return 'en';
  const base = normalized.split('-')[0];
  return base || 'en';
};

const getErrorMessage = (error: AxiosError) => {
  const data = error.response?.data as { message?: string; error?: string } | string | undefined;
  if (typeof data === 'string') {
    return data;
  }
  if (data && typeof data === 'object') {
    return data.message || data.error || error.message;
  }
  return error.message || 'Request failed';
};

axiosInstance.interceptors.request.use(config => {
  const locale = normalizeLocale(i18n.resolvedLanguage || i18n.language || 'en');
  const headers = AxiosHeaders.from(config.headers);
  if (!headers.has('x-locale')) {
    headers.set('x-locale', locale);
  }
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', locale);
  }
  config.headers = headers;
  startApiPerformanceTrace(config);
  return config;
});

axiosInstance.interceptors.response.use(
  response => {
    finishApiPerformanceTrace(response.config, response.status);
    return response;
  },
  error => {
    finishApiPerformanceTrace(error?.config, error?.response?.status, error?.code, true);
    if (error?.response?.status === 401 && _store) {
      _store.dispatch(logout());
      return Promise.reject(error);
    }

    const requestConfig = error?.config as AppAxiosRequestConfig | undefined;
    if (error?.code !== 'ERR_CANCELED' && !requestConfig?.skipErrorToast) {
      toast.error(getErrorMessage(error));
    }
    return Promise.reject(error);
  }
);

export const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      responseType?: AxiosRequestConfig['responseType'];
      headers?: AxiosRequestConfig['headers'];
      timeout?: AxiosRequestConfig['timeout'];
      timeoutErrorMessage?: AxiosRequestConfig['timeoutErrorMessage'];
      /** When `false`, the axios interceptor shows a global toast. Default suppresses (RTK callers usually unwrap and toast themselves). */
      suppressErrorToast?: boolean;
    },
    unknown,
    unknown
  > =>
  async (
    {
      url,
      method = 'GET',
      data,
      params,
      headers,
      responseType,
      timeout,
      timeoutErrorMessage,
      suppressErrorToast,
    },
    { getState }
  ) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;
      const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

      const result = await axiosInstance({
        url,
        method,
        data,
        params,
        responseType,
        timeout,
        timeoutErrorMessage,
        headers: { ...authHeader, ...(headers || {}) },
        skipErrorToast: suppressErrorToast !== false,
      } as AppAxiosRequestConfig);

      return { data: result.data };
    } catch (err) {
      const error = err as AxiosError;
      return {
        error: {
          status: error.response?.status,
          data: error.response?.data || error.message,
        },
      };
    }
  };
